/* WCF-095 linked customization service fees */
function decorateCustomizationPayload(payload, metadata) {
  if (!payload) return null;
  if (!metadata?.productTitle?.trim()) throw new Error('Missing custom product name');
  const result = structuredClone(payload);
  const parent = result.items[0];
  const props = parent.properties;
  const reference = props._wcf_customization_group.slice(-8);
  props['Customization reference'] = reference;
  // The fee product now collects the charge. Preserve details for production but
  // do not send EXPO's pricing attribute, which could double-charge ordinary products.
  props._wcf_customization_details = props._expo_properties_details;
  delete props._expo_properties_details;
  for (const fee of result.items.slice(1)) {
    const p = fee.properties;
    p._wcf_customization_type = p._wcf_customization_type === 'Embroidery'
      ? 'Embroidery Service Fee' : 'Logo Customization Service Fee';
    p['For product'] = metadata.productTitle.trim();
    if (metadata.variantTitle) p.Configuration = metadata.variantTitle;
    p['Customization reference'] = reference;
    if (p._wcf_customization_type === 'Embroidery Service Fee' && props['Embroidery Text']) {
      p['Embroidery text'] = props['Embroidery Text'];
    }
  }
  return result;
}
/* WCF-094 custom option charge bridge */
const WCF_CUSTOMIZATION_FEE_VARIANTS = Object.freeze({"embroidery":50553461375193,"logo":50553461539033});

function wcfParseExpoCustomizationSelections(rawValue) {
  if (typeof rawValue !== 'string' || rawValue.length === 0) return [];
  try {
    const details = JSON.parse(rawValue);
    if (!Array.isArray(details)) return [];
    const selected = new Set();
    for (const detail of details) {
      if (detail?.n === 'Embroidery' && detail?.v === 'Yes, add embroidery') selected.add('embroidery');
      if (detail?.n === 'Custom Logo' && detail?.v === 'Yes, add custom logo') selected.add('logo');
    }
    return ['embroidery', 'logo'].filter((key) => selected.has(key));
  } catch {
    return [];
  }
}

function wcfBuildCustomizationCartPayload(formData) {
  const properties = {};
  for (const [name, value] of formData.entries()) {
    const match = /^properties\[([^\]]+)\]$/.exec(name);
    if (match) properties[match[1]] = value;
  }
  const selections = wcfParseExpoCustomizationSelections(properties._expo_properties_details);
  if (selections.length === 0) return null;

  const parentId = Number(formData.get('id'));
  if (!Number.isSafeInteger(parentId) || parentId <= 0) throw new Error('Invalid custom product variant');
  const parsedQuantity = Number.parseInt(formData.get('quantity'), 10);
  const quantity = Number.isSafeInteger(parsedQuantity) && parsedQuantity > 0 ? parsedQuantity : 1;
  const uuid = globalThis.crypto?.randomUUID?.();
  const groupKey = uuid ? `wcf-${uuid}` : `wcf-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  properties._wcf_customization_group = groupKey;
  const parent = { id: parentId, quantity, properties };
  const sellingPlan = formData.get('selling_plan');
  if (sellingPlan) parent.selling_plan = sellingPlan;

  const labels = { embroidery: 'Embroidery', logo: 'Custom Logo' };
  const items = [parent];
  for (const key of selections) {
    items.push({
      id: WCF_CUSTOMIZATION_FEE_VARIANTS[key],
      quantity,
      properties: {
        _wcf_customization_fee: 'true',
        _wcf_customization_type: labels[key],
        _wcf_customization_group: groupKey,
        _wcf_customization_parent_variant: String(parentId),
      },
    });
  }

  const payload = { items };
  const sections = String(formData.get('sections') || '').split(',').map((value) => value.trim()).filter(Boolean);
  if (sections.length) payload.sections = sections;
  const sectionsUrl = formData.get('sections_url');
  if (sectionsUrl) payload.sections_url = sectionsUrl;
  return payload;
}

function wcfCartPayloadToFormData(payload) {
  const multiItemFormData = new FormData();
  payload.items.forEach((item, index) => {
    multiItemFormData.append(`items[${index}][id]`, String(item.id));
    multiItemFormData.append(`items[${index}][quantity]`, String(item.quantity));
    if (item.selling_plan) multiItemFormData.append(`items[${index}][selling_plan]`, String(item.selling_plan));
    for (const [name, value] of Object.entries(item.properties || {})) {
      multiItemFormData.append(`items[${index}][properties][${name}]`, value);
    }
  });
  if (payload.sections) multiItemFormData.append('sections', payload.sections);
  if (payload.sections_url) multiItemFormData.append('sections_url', payload.sections_url);
  return multiItemFormData;
}

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      resolveSelectedVariantId() {
        const productInfo = this.closest('product-info');
        const variantSelects = productInfo?.querySelector('variant-selects');
        if (!variantSelects) return null;

        const optionValues = [...variantSelects.querySelectorAll('fieldset, .product-form__input--dropdown')]
          .map((group) => group.querySelector('input:checked, select option[selected]')?.value)
          .filter(Boolean);
        const optionCount = variantSelects.querySelectorAll('fieldset, select').length;
        if (optionCount === 0 || optionValues.length !== optionCount) return null;

        try {
          const rawVariant = variantSelects.querySelector('[data-selected-variant]')?.textContent;
          const variant = rawVariant ? JSON.parse(rawVariant) : null;
          if (
            !variant?.id ||
            !Array.isArray(variant.options) ||
            variant.options.length !== optionValues.length ||
            !optionValues.every((value, index) => variant.options[index] === value)
          ) {
            return null;
          }
          return String(variant.id);
        } catch {
          return null;
        }
      }
      onSubmitHandler(evt) {
        evt.preventDefault();
        if (!this.variantIdInput.value) {
          const resolvedVariantId = this.resolveSelectedVariantId();
          if (resolvedVariantId) this.variantIdInput.value = resolvedVariantId;
        }
        const productInfo = this.closest('product-info');
        const isCustomSizedProduct = Boolean(
          productInfo?.querySelector('script[data-wcf-custom-product-options]'),
        );
        if (!this.variantIdInput.value) {
          const errorMessage = isCustomSizedProduct
            ? 'Please choose a color.'
            : 'Please choose a size.';
          this.handleErrorMessage(errorMessage);
          this.dispatchCartErrorEvent(errorMessage, 'INVALID');
          return;
        }
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        const customizationPayload = isCustomSizedProduct
          ? wcfBuildCustomizationCartPayload(formData)
          : null;
        if (customizationPayload) {
          let selectedVariant = null;
          try { selectedVariant = JSON.parse(productInfo.querySelector('[data-selected-variant]')?.textContent || 'null'); } catch {}
          const productTitle = productInfo.querySelector('.product__title h1, .product__title h2, h1')?.textContent?.trim();
          const variantTitle = selectedVariant?.options?.join(' / ') || '';
          config.body = wcfCartPayloadToFormData(decorateCustomizationPayload(customizationPayload, {productTitle,variantTitle}));
        } else {
          config.body = formData;
        }

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              const errorMessage = !formData.get('id') ? 'Please choose a size.' : response.description;
              this.handleErrorMessage(errorMessage);
              this.dispatchCartErrorEvent(errorMessage || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(errorMessage || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}

